import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { api, type MeetingGuideResult } from '../lib/api.ts'
import { brandToMergeFields, useBrandKit } from '../brand/BrandKitContext.tsx'
import { renderMarkdown } from '../lib/markdown.ts'
import { RenderedDoc } from './RenderedDoc.tsx'

/**
 * The topic's staff meeting guide: a deterministic agenda, plus the only
 * model-generated content in this app — discussion questions. `aiQuestions`
 * goes into RenderedDoc's `extra` slot under a visible "AI-assisted" label; it
 * is never concatenated into `markdown`, so the deterministic body stays
 * exactly what the server authored.
 */
export function MeetingGuidePage() {
  const { topicId = '' } = useParams()
  const { brand } = useBrandKit()
  const [guide, setGuide] = useState<MeetingGuideResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setGuide(null)
    setError(null)
    api.meetingGuide(topicId, brandToMergeFields(brand), true)
      .then(setGuide)
      .catch(() => setError('Could not load this meeting guide. Please try again.'))
  }, [topicId, brand])

  if (error) return <main className="tool"><p className="notice notice--error">{error}</p></main>
  if (!guide) return <main className="tool"><p className="notice">Loading…</p></main>

  return (
    <RenderedDoc
      title={guide.title}
      markdown={guide.markdown}
      disclaimer={guide.disclaimer}
      reviewStatus={guide.reviewStatus}
      // The topic's own name, straight off the topics row (server/meeting-guide.ts).
      crumbs={[
        { label: 'Departments', to: '/' },
        { label: guide.title, to: `/topics/${topicId}` },
        { label: 'Meeting guide' },
      ]}
      extra={guide.aiQuestions && (
        <section className="ai-section">
          <p className="eyebrow">AI-assisted, review before use</p>
          <div className="document" dangerouslySetInnerHTML={{ __html: renderMarkdown(guide.aiQuestions) }} />
        </section>
      )}
    />
  )
}
